import { Component } from '@theme/component';
import { showToast } from '@theme/r-toast';

/**
 * @theme/r-chat-launcher — the Gorgias chat bubble, loaded on demand (WS20).
 *
 * The Gorgias app embed downloads the whole chat app (~640 KB, ~370 ms of main
 * thread on a mid-range phone) on every page view, whether or not anyone chats.
 * This element stands in for Gorgias's launcher — same place, same size, same
 * icon — and injects the very loader the embed would have injected only when the
 * shopper reaches for it: pointer over or focus on the bubble (or a "Start a live
 * chat" trigger) starts the download, a click opens the chat once it is ready.
 * Gorgias's own bubble then takes over in the same spot and this one steps aside.
 *
 * Stands down entirely while the app embed is still on (its loader tag is in the
 * head before this module runs), so turning the embed off in the theme editor is
 * the switch — no deploy needed either way. Full rationale and trade-offs:
 * .cursor/plans/WS20-third-party-apps-2026-10-01.md §3.
 */

/** The app embed's own loader tag — present when the embed is on. */
const EMBED_SELECTOR = 'script[data-gorgias-loader-chat]';

/**
 * Give up on a load that never announces itself, so a click is never left hanging.
 * Generous: the chat app is ~640 KB, which takes several seconds on a slow phone link.
 */
const LOAD_TIMEOUT_MS = 20000;

/** A conversation this recent loads the real widget at once, so replies and unread badges keep working. */
const RECENT_CONVERSATION_MS = 24 * 60 * 60 * 1000;

/** Gorgias's own record of the last message in the visitor's conversation (localStorage, same origin). */
const LAST_MESSAGE_KEY = 'gorgias.last-message-timestamp';

/** Gorgias's own launcher, rendered into its container once the widget is up. */
const GORGIAS_LAUNCHER_SELECTOR = '#gorgias-chat-container #chat-button';

/** How long to wait for Gorgias's launcher to appear before stepping aside anyway. */
const HANDOFF_WAIT_MS = 3000;

/** Gorgias fades its bubble in; hold ours until that has finished so the swap never shows a gap. */
const HANDOFF_DELAY_MS = 400;

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** @type {Promise<void> | null} */
let loading = null;

/** The loader tags go in once per page; a retry after a timeout only waits again. */
let injected = false;

/** @returns {any} */
const gorgias = () => /** @type {any} */ (window).GorgiasChat;

/** @returns {boolean} */
function embedActive() {
  return Boolean(gorgias() || document.querySelector(EMBED_SELECTOR));
}

/** @returns {RChatLauncher | null} */
function launcher() {
  const element = document.querySelector('r-chat-launcher');
  return element instanceof RChatLauncher ? element : null;
}

/** @returns {boolean} */
function hasRecentConversation() {
  try {
    const raw = localStorage.getItem(LAST_MESSAGE_KEY);
    if (!raw) return false;
    const time = Number.isNaN(Number(raw)) ? Date.parse(raw) : Number(raw);
    return Number.isFinite(time) && Date.now() - time < RECENT_CONVERSATION_MS;
  } catch {
    return false;
  }
}

/**
 * Appends the same loaders the app embed would have: chat, and Gorgias Convert
 * (campaigns) when enabled. Convert finds its own base URL by looking for its
 * `data-gorgias-loader-convert` tag, so it keeps that attribute; the chat loader
 * deliberately does not carry the embed's attribute, or chatOnDemand() would
 * mistake it for the embed.
 *
 * @param {RChatLauncher} element
 * @param {() => void} onChatError
 */
function injectLoaders(element, onChatError) {
  if (injected) return;
  injected = true;

  const { chatLoader, convertLoader } = element.dataset;
  if (!chatLoader) {
    onChatError();
    return;
  }

  const chat = document.createElement('script');
  chat.src = chatLoader;
  chat.async = true;
  chat.addEventListener('error', onChatError, { once: true });
  document.head.append(chat);

  if (convertLoader) {
    const convert = document.createElement('script');
    convert.src = convertLoader;
    convert.async = true;
    convert.setAttribute('data-gorgias-loader-convert', '');
    document.head.append(convert);
  }
}

/**
 * True when chat is served on demand by the launcher — the app embed is off and
 * the launcher rendered. Triggers elsewhere use it to decide how to open chat.
 *
 * @returns {boolean}
 */
export function chatOnDemand() {
  return Boolean(launcher()) && !document.querySelector(EMBED_SELECTOR);
}

/**
 * Injects the Gorgias loaders once and resolves when the widget is ready.
 * Safe to call repeatedly; every caller shares one load.
 *
 * @returns {Promise<void>}
 */
export function loadChat() {
  if (loading) return loading;

  const element = launcher();
  if (!element) return Promise.reject(new Error('r-chat-launcher is not on the page'));

  loading = new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error('Gorgias did not load')), LOAD_TIMEOUT_MS);
    const ready = async () => {
      try {
        await gorgias().init();
        clearTimeout(timeout);
        resolve();
      } catch (error) {
        reject(error);
      }
    };

    if (gorgias()) {
      ready();
      return;
    }

    window.addEventListener('gorgias-widget-loaded', ready, { once: true });
    injectLoaders(element, () => reject(new Error('The Gorgias loader failed')));
  });

  // A failed load may be retried by the next click.
  loading.catch(() => {
    loading = null;
  });

  return loading;
}

/**
 * Loads chat if needed and opens it. On failure, says so instead of failing silently.
 *
 * @returns {Promise<boolean>} whether the chat opened
 */
export async function openChat() {
  try {
    await loadChat();
    gorgias().open();
    return true;
  } catch {
    showToast(launcher()?.dataset.unavailableMessage ?? '', { tone: 'error' });
    return false;
  }
}

/**
 * @typedef {object} ChatLauncherRefs
 * @property {HTMLButtonElement} button
 */

/**
 * @extends {Component<ChatLauncherRefs>}
 */
export class RChatLauncher extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController | null} */
  #listeners = null;

  #handingOff = false;

  connectedCallback() {
    super.connectedCallback();

    if (embedActive()) {
      this.remove();
      return;
    }

    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const preload = () => this.#preload();

    for (const type of ['pointerenter', 'focus', 'touchstart']) {
      this.refs.button.addEventListener(type, preload, { once: true, passive: true, signal });
    }
    this.refs.button.addEventListener('click', () => this.open(), { signal });
    window.addEventListener('gorgias-widget-loaded', () => this.#handOff(), { once: true, signal });

    this.hidden = false;

    if (hasRecentConversation()) {
      this.#afterLoad(preload);
    } else if (this.dataset.mode === 'engaged') {
      this.#afterLoad(() => this.#onFirstInteraction(preload));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  async open() {
    const { button } = this.refs;
    if (button.getAttribute('aria-busy') === 'true') return;

    button.setAttribute('aria-busy', 'true');
    const opened = await openChat();
    button.removeAttribute('aria-busy');
    if (opened) this.#handOff();
  }

  #preload() {
    loadChat().catch(() => {});
  }

  /**
   * Gorgias's bubble renders in the same spot — step aside once it is there and
   * its fade-in has finished. If Gorgias renders no launcher (its own settings
   * can hide it), step aside anyway: that is Gorgias's decision to show no chat.
   */
  async #handOff() {
    if (this.#handingOff) return;
    this.#handingOff = true;

    const deadline = Date.now() + HANDOFF_WAIT_MS;
    while (Date.now() < deadline && !document.querySelector(GORGIAS_LAUNCHER_SELECTOR)) {
      await wait(100);
    }
    await wait(HANDOFF_DELAY_MS);
    this.hidden = true;
  }

  /** @param {() => void} callback */
  #afterLoad(callback) {
    const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(callback, { timeout: 3000 }) : setTimeout(callback, 1));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true, signal: this.#listeners?.signal });
  }

  /** @param {() => void} callback */
  #onFirstInteraction(callback) {
    const controller = new AbortController();
    const once = () => {
      controller.abort();
      callback();
    };
    for (const type of ['scroll', 'pointerdown', 'keydown']) {
      window.addEventListener(type, once, { passive: true, signal: controller.signal });
    }
    this.#listeners?.signal.addEventListener('abort', () => controller.abort());
  }
}

if (!customElements.get('r-chat-launcher')) {
  customElements.define('r-chat-launcher', RChatLauncher);
}
